import React, { useEffect, useRef, useState } from "react";
import { AppointmentType } from "../types";
import "../pages/PurchaseOrders.css";
import "../pages/Appointments.css";

interface Props {
  initial: AppointmentType;
  roles: { roleId: number; name: string; userCount?: number }[];
  busy: boolean;
  onClose: () => void;
  onSave: (draft: AppointmentType) => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
};

/** Randevu türü ekle / düzenle. Süre ve görüşme şekli türden gelir; tür kişiye değil role bağlanır. */
const AppointmentTypeModal: React.FC<Props> = ({ initial, roles, busy, onClose, onSave }) => {
  const [draft, setDraft] = useState<AppointmentType>(initial);
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const firstRef = useRef<HTMLInputElement | null>(null);
  const valid = !!draft.name.trim() && draft.durationMinutes > 0;

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (busy || !valid) return;
    onSave(draft);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) { e.stopPropagation(); onClose(); }
      else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, busy, draft]);

  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  useEffect(() => { firstRef.current?.focus(); }, []);

  const toggleRole = (roleId: number, on: boolean) =>
    setDraft({ ...draft, roleIds: on ? [...draft.roleIds, roleId] : draft.roleIds.filter((x) => x !== roleId) });

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="ap-type-title" onSubmit={submit}>
        <div className="po-modal-head">
          <div><span className="po-kicker">Randevu türü</span><h3 id="ap-type-title">{draft.appointmentTypeId ? "Türü düzenle" : "Yeni randevu türü"}</h3></div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={busy}>{I.close}</button>
        </div>
        <div className="po-modal-body">
          <p className="po-form-hint">Dışarıdaki kişi süre seçmez, tür seçer. Süre ve görüşme şekli bu türden gelir.</p>
          <label className="po-field">
            <span>Ad<em>zorunlu</em></span>
            <input ref={firstRef} value={draft.name} maxLength={100} placeholder="Numune görüşmesi" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label className="po-field">
            <span>Açıklama<em>isteğe bağlı</em></span>
            <textarea rows={2} maxLength={500} value={draft.description ?? ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </label>
          <div className="po-grid2">
            <label className="po-field">
              <span>Süre<em>dakika</em></span>
              <input type="number" min={5} step={5} value={draft.durationMinutes} onChange={(e) => setDraft({ ...draft, durationMinutes: Number(e.target.value) })} />
            </label>
            <label className="po-field">
              <span>Görüşme şekli</span>
              <select value={draft.modeOption} onChange={(e) => setDraft({ ...draft, modeOption: Number(e.target.value) as 1 | 2 | 3 })}>
                <option value={3}>Yüz yüze veya çevrim içi</option>
                <option value={1}>Yalnız yüz yüze</option>
                <option value={2}>Yalnız çevrim içi</option>
              </select>
            </label>
          </div>
          <label className="po-check">
            <input type="checkbox" checked={draft.isPublic} onChange={(e) => setDraft({ ...draft, isPublic: e.target.checked })} />
            Genel randevu sayfasında görünsün
          </label>
          <div className="po-field">
            <span>Bu türü karşılayan roller<em>{draft.roleIds.length} seçili</em></span>
            <div className="ap-roles">
              {roles.length === 0 && <span className="po-fieldnote">Rol listesi alınamadı.</span>}
              {roles.map((r) => (
                <label key={r.roleId} className="po-check">
                  <input type="checkbox" checked={draft.roleIds.includes(r.roleId)} onChange={(e) => toggleRole(r.roleId, e.target.checked)} />
                  {r.name}
                  {typeof r.userCount === "number" && <small>{r.userCount} kişi</small>}
                </label>
              ))}
            </div>
            <span className="po-fieldnote">Bu roldeki herkes bu türü karşılar. Kişinin randevuya açık olup olmadığını Randevu sayfam sekmesindeki yayın anahtarı belirler.</span>
          </div>
        </div>
        <div className="po-modal-foot">
          <button type="button" className="po-btn" onClick={onClose} disabled={busy}>Vazgeç</button>
          <button type="submit" className="po-btn primary" disabled={busy || !valid}>{busy ? "Kaydediliyor" : "Kaydet"}</button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentTypeModal;
